import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import './AgreementModal.css';
import { parseAgreement, splitBold } from '../../../../content/userAgreement';

/**
 * Full-size reader for a user agreement. Agreeing is an explicit click that is
 * only available once the document has been scrolled to the end; closing any
 * other way leaves the agreement unaccepted.
 */
const AgreementModal = ({ agreement, isOpen, onClose, onAgree }) => {
    const scrollRef = useRef(null);
    const [blocks, setBlocks] = useState([]);
    const [loadedUrl, setLoadedUrl] = useState(null);
    const [status, setStatus] = useState('loading');
    const [read, setRead] = useState(false);

    // Fetched the first time the document is opened rather than on page load,
    // so the signup form does not pay for a document nobody may open.
    useEffect(() => {
        if (!isOpen || loadedUrl === agreement.url) return undefined;

        let cancelled = false;
        setStatus('loading');
        setBlocks([]);
        setRead(false);

        fetch(agreement.url)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then((text) => {
                if (cancelled) return;
                setBlocks(parseAgreement(text));
                setLoadedUrl(agreement.url);
                setStatus('ready');
            })
            .catch((error) => {
                if (cancelled) return;
                console.log('[userAgreement]', error);
                setStatus('error');
            });

        return () => { cancelled = true; };
    }, [isOpen, loadedUrl, agreement.url]);

    // A short document or a tall window may not scroll at all, which would
    // otherwise leave the agree button permanently disabled.
    useEffect(() => {
        if (!isOpen || blocks.length === 0) return;
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollHeight - el.clientHeight <= 4) setRead(true);
    }, [isOpen, blocks]);

    const handleScroll = (el) => {
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= 4) setRead(true);
    }

    const handleAgree = () => {
        onAgree();
        onClose();
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel={agreement.title}
            ariaHideApp={false}
            className="agreement-modal"
            overlayClassName="agreement-modal-overlay"
        >
            <div className="agreement-modal-header">
                <div>
                    <h2 className="agreement-modal-title">{agreement.title}</h2>
                    <p className="agreement-modal-subtitle">
                        Read the full document. The agree button below activates
                        once you reach the end.
                    </p>
                </div>
                <button
                    type="button"
                    className="agreement-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    &times;
                </button>
            </div>

            <div
                className="agreement-modal-body"
                ref={scrollRef}
                onScroll={(e) => handleScroll(e.currentTarget)}
                tabIndex={0}
            >
                {status === 'loading' && (
                    <p className="agreement-modal-text">Loading the agreement...</p>
                )}
                {status === 'error' && (
                    <p className="agreement-modal-text">
                        The agreement could not be loaded. Please close this window
                        and try again.
                    </p>
                )}
                {blocks.map((block, index) => (
                    <p
                        className={
                            block.type === 'p'
                                ? 'agreement-modal-text'
                                : `agreement-modal-doc-heading ${block.type}`
                        }
                        key={index}
                    >
                        {splitBold(block.text).map((run, runIndex) =>
                            run.bold ? (
                                <strong key={runIndex}>{run.text}</strong>
                            ) : (
                                <React.Fragment key={runIndex}>{run.text}</React.Fragment>
                            )
                        )}
                    </p>
                ))}
            </div>

            <div className="agreement-modal-footer">
                <p className="agreement-modal-hint">
                    {read
                        ? 'You have reached the end of the document.'
                        : 'Scroll to the end of the document to continue.'}
                </p>
                <button
                    type="button"
                    className="agreement-modal-agree"
                    onClick={handleAgree}
                    disabled={!read || status !== 'ready'}
                >
                    I Agree
                </button>
            </div>
        </Modal>
    )
}

export default AgreementModal;
